import { BKJLoadingSpinner } from '@bookjane2/bookjane-design-library';
import {
  ActivityTabTableStyled,
  ActivityTabTableWrapper,
  TableHeaderCell,
} from 'components/ActivityTab/ActivityTab.styled';
import { DesktopAuditTrailActivityTabRow } from 'components/AuditTrailActivityTab/DesktopAuditTrailActivityTabRow';
import { ActivityTabRowPropsProvider } from 'components/ActivityTab/ActivityTabRow.props.context';
import {
  ActivitiesDataProvider,
  useActivitiesDataContext,
} from 'components/ActivityTab/ActivityTab.data.context';
import { Flex } from 'components/Flex';
import { InfiniteScrollLoading } from 'components/InfiniteScrollLoading';
import { FC, Fragment } from 'react';
import { useTranslation } from 'hooks/useTranslation';
import { MountTransition } from 'routes';
import { UserType } from 'types/activities.types';
import { useResponsive } from 'providers/ResponsiveProvider/Responsive';
import { MobileAuditTrailActivityTabRow } from 'components/AuditTrailActivityTab/MobileAuditTrailActivityTabRow';
import type { ShiftModalDetailsData } from 'modals/ShiftModal/ShiftModal.constants';

const AuditTrailActivityTabInner: FC<{ timeZone?: string }> = ({ timeZone, children }) => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const { activities, isLoading, hasNextPage, loadMore } = useActivitiesDataContext();
  const AuditTrailActivityTabRow = isMobile
    ? MobileAuditTrailActivityTabRow
    : DesktopAuditTrailActivityTabRow;

  return (
    <Fragment>
      <ActivityTabTableWrapper>
        <MountTransition>
          <ActivityTabTableStyled>
            {!isMobile && (
              <Fragment>
                <TableHeaderCell>{t('common:TIME')}</TableHeaderCell>
                <TableHeaderCell>{t('common:ACTIVITY')}</TableHeaderCell>
                <TableHeaderCell>{t('dashboard:DETAILS')}</TableHeaderCell>
              </Fragment>
            )}
            {activities.map(({ createdAt, event, user, metadata }, index) => {
              const userDetails = { ...user } as UserType;

              return (
                <ActivityTabRowPropsProvider
                  timeZone={timeZone}
                  key={createdAt + event.value}
                  createdAt={createdAt}
                  event={event}
                  userDetails={userDetails}
                  index={index}
                  metadata={metadata ?? null}
                >
                  <AuditTrailActivityTabRow />
                </ActivityTabRowPropsProvider>
              );
            })}

            {hasNextPage && (
              <InfiniteScrollLoading onViewportEnter={loadMore} style={{ gridColumn: '1 / -1' }} />
            )}
          </ActivityTabTableStyled>
          {isLoading && (
            <Flex justifyContent="center" padding="24px">
              <BKJLoadingSpinner variant="Default" />
            </Flex>
          )}
        </MountTransition>
      </ActivityTabTableWrapper>
      {children}
    </Fragment>
  );
};

export const AuditTrailActivityTab: FC<{
  data: ShiftModalDetailsData;
  timeZone?: string;
}> = ({ data, timeZone, children }) => {
  return (
    <ActivitiesDataProvider orderId={data.order.relayId}>
      <AuditTrailActivityTabInner timeZone={timeZone}>{children}</AuditTrailActivityTabInner>
    </ActivitiesDataProvider>
  );
};
